import { globalAyahNumber, TOTAL_QURAN_VERSES } from './quranIndex';

// What was actually read, day by day.
//
// A counter would drift: re-reading a passage would inflate it, and mashing
// Next would too. Storing which ayahs were read makes re-reads free and lets
// every unit — verses, surahs, juz — be derived from the same record.
//
// { v: 2, days: { '2026-09-11': [262, 263, 264] }, khatms: 0, cycleStart: '2026-09-01' }
//
// Values are global ayah numbers, sorted and deduplicated within a day.
//
// Deduplication is what makes re-reading free, but it also means a second
// complete reading would never show up. So the log also tracks cycles: the
// current one runs from cycleStart, and when every one of the 6236 ayahs has
// been read inside it, khatms goes up by one and a fresh cycle opens the next
// day. Opening it on the completing day instead would hand the whole finished
// reading straight to the new cycle, which then completes immediately.

const STORAGE_KEY = 'quranReaderReadingLog';
const SCHEMA_VERSION = 2;

const emptyLog = () => ({ v: SCHEMA_VERSION, days: {}, khatms: 0, cycleStart: null });

// Local calendar date, not UTC: a day should end at the reader's midnight.
export const dateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// The calendar day after the given key, as YYYY-MM-DD.
const dayAfter = (key) => {
  const date = new Date(`${key}T00:00:00`);
  date.setDate(date.getDate() + 1);
  return dateKey(date);
};

export const isValidLog = (value) =>
  Boolean(value) && typeof value === 'object' && typeof value.days === 'object' && value.days !== null;

// The earliest day with anything in it, used as the opening of the first cycle.
const earliestDay = (days) => {
  const keys = Object.keys(days).filter((key) => (days[key] || []).length > 0).sort();
  return keys[0] || null;
};

// Logs written before cycles existed get one that opens at their first reading.
export const migrateLog = (log) => {
  if (log.v === SCHEMA_VERSION && log.cycleStart !== undefined) return log;
  return {
    v: SCHEMA_VERSION,
    days: log.days || {},
    khatms: Number.isInteger(log.khatms) ? log.khatms : 0,
    cycleStart: log.cycleStart || earliestDay(log.days || {})
  };
};

export const loadLog = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyLog();
    const parsed = JSON.parse(raw);
    if (!isValidLog(parsed)) return emptyLog();
    return migrateLog(parsed);
  } catch (error) {
    console.error('Could not read the reading log:', error);
    return emptyLog();
  }
};

export const saveLog = (log) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(log));
    return true;
  } catch (error) {
    // A full quota should never cost the reader their place in the text
    console.error('Could not save the reading log:', error);
    return false;
  }
};

// Returns a new log with this verse recorded, or the same object when there is
// nothing to add — so callers can skip a write when the reference is unchanged.
export const withVerseRecorded = (log, surah, verse, date = new Date()) => {
  const ayah = globalAyahNumber(surah, verse);
  if (!ayah) return log;

  const key = dateKey(date);
  const existing = log.days[key] || [];
  if (existing.includes(ayah)) return log;

  const updated = [...existing, ayah].sort((a, b) => a - b);
  const next = {
    ...log,
    days: { ...log.days, [key]: updated },
    cycleStart: log.cycleStart || key
  };

  // That verse may have been the last one missing from this reading
  if (cycleAyahs(next).size >= TOTAL_QURAN_VERSES) {
    return { ...next, khatms: (next.khatms || 0) + 1, cycleStart: dayAfter(key) };
  }
  return next;
};

export const ayahsReadOn = (log, key) => log.days[key] || [];

// Every ayah read with a date in [startKey, endKey], inclusive. String
// comparison is enough: YYYY-MM-DD sorts chronologically.
export const ayahsReadBetween = (log, startKey, endKey) => {
  const seen = new Set();
  Object.keys(log.days).forEach((key) => {
    if (key >= startKey && key <= endKey) {
      log.days[key].forEach((ayah) => seen.add(ayah));
    }
  });
  return seen;
};

export const ayahsReadUpTo = (log, endKey) => ayahsReadBetween(log, '0000-00-00', endKey);

// Distinct ayahs read inside the cycle in progress.
export const cycleAyahs = (log) =>
  ayahsReadBetween(log, log.cycleStart || '0000-00-00', '9999-99-99');

export const cycleProgress = (log) => {
  const read = cycleAyahs(log).size;
  return { read, total: TOTAL_QURAN_VERSES, percent: (read / TOTAL_QURAN_VERSES) * 100 };
};

export const khatmCount = (log) => log.khatms || 0;

export const totalAyahsRead = (log) => ayahsReadUpTo(log, '9999-99-99').size;

export const daysWithReading = (log) =>
  Object.keys(log.days).filter((key) => log.days[key].length > 0).sort();

export const clearLog = () => {
  localStorage.removeItem(STORAGE_KEY);
  return emptyLog();
};

export { emptyLog, STORAGE_KEY as READING_LOG_KEY, SCHEMA_VERSION, TOTAL_QURAN_VERSES };
